import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Images, Film } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { UpcomingBirthdays } from "../components/UpcomingBirthdays";

const CARD_HIDDEN = { opacity: 0, y: 32 };
const CARD_VISIBLE = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, margin: "-40px" };
const cardTransition = (i) => ({ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] });

const Dogs = () => {
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dogs").then(({ data }) => setDogs(data)).finally(() => setLoading(false));
  }, []);

  return (
    <main className="mx-auto max-w-6xl px-4 pb-24 sm:px-8">
      <section className="py-14 sm:py-20">
        <Reveal>
          <p className="eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#B85328]" />
            I protagonisti
          </p>
          <h1
            data-testid="dogs-main-heading"
            className="font-display mt-4 max-w-2xl text-4xl font-bold leading-[1.08] tracking-tight text-[#2C2723] sm:text-5xl lg:text-6xl"
          >
            I bassotti della <span className="italic text-[#B85328]">community</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5C534B] sm:text-lg">
            Ogni bassotto ha il suo diario di crescita: tutti i ricordi condivisi dalla sua famiglia,
            dal primo giorno a oggi.
          </p>
        </Reveal>
      </section>

      <UpcomingBirthdays />

      {loading && <p className="py-20 text-center font-display text-2xl italic text-[#8C7B6E]">Caricamento...</p>}
      {!loading && dogs.length === 0 && (
        <div data-testid="dogs-empty-state" className="rounded-3xl border border-dashed border-[#CFC0B2] py-24 text-center">
          <p className="font-display text-3xl italic text-[#5C534B]">Nessun diario ancora aperto.</p>
          <p className="mt-3 text-sm text-[#8C7B6E]">I diari nascono quando i primi ricordi vengono approvati.</p>
        </div>
      )}
      {!loading && dogs.length > 0 && (
        <div data-testid="dogs-grid" className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {dogs.map((dog, i) => (
            <motion.div
              key={dog.dog_name}
              initial={CARD_HIDDEN}
              whileInView={CARD_VISIBLE}
              viewport={CARD_VIEWPORT}
              transition={cardTransition(i)}
            >
              <Link
                to={`/cani/${encodeURIComponent(dog.dog_name)}`}
                data-testid={`dog-card-${dog.dog_name.toLowerCase()}`}
                className="group block overflow-hidden rounded-3xl border border-[#E8DFD5] bg-white transition-all duration-300 hover:-translate-y-1.5 hover:border-[#B85328]/40 hover:shadow-xl"
              >
                <div className="relative overflow-hidden">
                  {dog.cover_type === "video" ? (
                    <div className="relative">
                      <video src={mediaUrl(dog.cover_url)} muted playsInline preload="metadata" className="aspect-[4/5] w-full object-cover" />
                      <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-[#1F1A17]/80 text-[#FBF8F3]">
                        <Film size={15} />
                      </span>
                    </div>
                  ) : (
                    <img
                      src={mediaUrl(dog.cover_url)}
                      alt={`${dog.dog_name} — diario di crescita`}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-semibold text-[#2C2723] transition-colors group-hover:text-[#B85328]">
                    {dog.dog_name}
                  </h3>
                  <div className="mt-3 flex items-center gap-4 text-xs font-semibold text-[#8C7B6E]">
                    <span className="flex items-center gap-1.5">
                      <Images size={13} /> {dog.memories_count} {dog.memories_count === 1 ? "ricordo" : "ricordi"}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Heart size={13} /> {dog.likes_total}
                    </span>
                  </div>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B85328]">
                    Apri il diario →
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </main>
  );
};

export default Dogs;
